import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { deleteCompany } from "@/services/companyManagement";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { CompanyTableData } from "./companies-table-columns";

interface DeleteCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyTableData;
  onSuccess: () => void;
}

export function DeleteCompanyDialog({
  open,
  onOpenChange,
  company,
  onSuccess,
}: DeleteCompanyDialogProps) {
  const queryClient = useQueryClient();
  const [hardDelete, setHardDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteCompanyMutation = useMutation({
    mutationFn: (id: string) => deleteCompany(id, hardDelete),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onSuccess();
      setHardDelete(false); // Reset hard delete option
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete company");
    },
    onSettled: () => {
      setIsDeleting(false);
    },
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCompanyMutation.mutateAsync(company.id);
    } catch (error) {
      // Error is handled in mutation onError
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isDeleting) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Delete Company
          </DialogTitle>
          <DialogDescription>
            {hardDelete 
              ? `Are you sure you want to PERMANENTLY DELETE "${company.name}"? This action cannot be undone and all data will be lost forever.`
              : `Are you sure you want to delete "${company.name}"? This will mark the company as deleted but data can be recovered.`
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Hard Delete Option */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hard-delete"
              checked={hardDelete}
              onCheckedChange={(checked) => setHardDelete(checked as boolean)}
            />
            <Label htmlFor="hard-delete" className="text-sm font-medium">
              Permanently delete (cannot be recovered)
            </Label>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">Warning:</p>
                <ul className="list-disc list-inside space-y-1">
                  {hardDelete ? (
                    <>
                      <li>This will PERMANENTLY DELETE the company and all data</li>
                      <li>All associated data will be completely removed:</li>
                      <li className="ml-4">• Users, Orders, Transactions</li>
                      <li className="ml-4">• Car Services, Categories, Sub-categories</li>
                      <li className="ml-4">• Scanned Vehicles, Contact Messages</li>
                      <li className="font-bold text-red-900">⚠️ This action CANNOT be undone!</li>
                    </>
                  ) : (
                    <>
                      <li>This will mark the company as deleted (soft delete)</li>
                      <li>All associated data will be marked as deleted:</li>
                      <li className="ml-4">• Users, Orders, Transactions</li>
                      <li className="ml-4">• Car Services, Categories, Sub-categories</li>
                      <li className="ml-4">• Scanned Vehicles, Contact Messages</li>
                      <li>Data can be recovered by restoring the company</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
{isDeleting ? "Deleting..." : hardDelete ? "Permanently Delete" : "Delete Company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
